import React from 'react';
import { createPortal } from 'react-dom';

type ClampedTextWithTooltipProps = {
  text: string | null | undefined;
  className?: string;
  emptyLabel?: string;
};

export function ClampedTextWithTooltip({
  text,
  className = '',
  emptyLabel = '—',
}: ClampedTextWithTooltipProps) {
  const display = text?.trim() || '';
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{
    top: number;
    left: number;
    placement: 'above' | 'below';
  } | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = React.useId();

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const updatePos = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: 'above' | 'below' =
      spaceBelow >= 140 || rect.top < 140 ? 'below' : 'above';
    setPos({
      top: placement === 'below' ? rect.bottom + gap : rect.top - gap,
      left: rect.left,
      placement,
    });
  }, []);

  const show = React.useCallback(() => {
    if (!display) return;
    clearCloseTimer();
    updatePos();
    setOpen(true);
  }, [display, updatePos, clearCloseTimer]);

  const hideSoon = React.useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  }, [clearCloseTimer]);

  React.useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  React.useEffect(() => {
    if (!open) return;
    updatePos();
    const onReposition = () => updatePos();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updatePos]);

  const tooltip =
    open && display && pos && typeof document !== 'undefined'
      ? createPortal(
          <div
            id={tipId}
            role="tooltip"
            onMouseEnter={show}
            onMouseLeave={hideSoon}
            className="fixed z-[9999] w-max max-w-[min(360px,calc(100vw-24px))] max-h-[min(50vh,16rem)] overflow-y-auto overscroll-contain rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs leading-relaxed text-app-text-muted shadow-lg"
            style={{
              top: pos.top,
              left: pos.left,
              transform: pos.placement === 'above' ? 'translateY(-100%)' : undefined,
            }}
          >
            <p className="m-0 whitespace-pre-wrap break-words">{display}</p>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={`line-clamp-2 ${display ? 'cursor-default' : ''} ${className}`.trim()}
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onFocus={display ? show : undefined}
        onBlur={display ? hideSoon : undefined}
        tabIndex={display ? 0 : undefined}
        aria-describedby={open ? tipId : undefined}
      >
        {display || emptyLabel}
      </span>
      {tooltip}
    </>
  );
}
